import '../styles/SignUp.css';

import { Box, Card, CardContent, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { addPersonalDetails, register, resend, verifyEmail } from '../services/SignUpApi';
import { startFreeTrial, updateUserDetails } from '../services/PersonalDetailsApi';
import {
	updateCurrentPlanStartDate,
	updateEmailId,
	updateFirstName,
	updateIsAuthenticated,
	updateLastName,
	updatePhoneNumber,
	updatePlanTierId,
	updateStripeCustomerId,
	updateUserId,
} from '../store/userDetailsSlice';

import Button from '../components/atoms/Button';
import Cookies from 'js-cookie';
import DropdownWithImage from '../components/atoms/DropDownWithImage';
import Label from '../components/atoms/Label';
import { PURCHASE_TOGGLE } from '../constants/constants';
import Plan from '../components/templates/Plans';
import TextFieldWithImage from '../components/atoms/TextFieldWithImage';
import { allowAccessToPaymentStatusPage } from '../store/navigationSlice';
import city from '../assets/ic_city.svg';
import { createStripeUser } from '../services/PaymentService';
import email from '../assets/email.svg';
import email_colored_green from '../assets/email_colored_green.svg';
import exam from '../assets/exam_drop_down_image.svg';
import { logger } from '../utils/logger';
import passwordImage from '../assets/lock.svg';
import phone from '../assets/ic_phone.svg';
import signupImage from '../assets/signup_image.svg';
import test from '../assets/ic_test.svg';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import userIcon from '../assets/user_icon.svg';

const SignUp = ({ openLoginModal, setEmail, closeSignUpModal }) => {
	const [currentStep, setCurrentStep] = useState(1);
	const [enterEmail, setEnterEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [username, setUsername] = useState('');

	const [errorMessage, setErrorMessage] = useState('');

	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [phoneNumber, setPhone] = useState('');
	const [_city, setCity] = useState('');
	const [_state, setState] = useState('');
	const [_country, setCountry] = useState('');
	const [_exam, setExam] = useState('');

	const [timer, setTimer] = useState(300);
	const [isResendDisabled, setIsResendDisabled] = useState(true);
	const [step2IsLoading, step2setIsLoading] = useState(false);
	const [step3IsLoading, step3setIsLoading] = useState(false);
	const [step4IsLoading, step4setIsLoading] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	useEffect(() => {
		let interval;
		if (currentStep === 2 && timer > 0) {
			interval = setInterval(() => {
				setTimer((prev) => prev - 1);
			}, 1000);
		} else if (timer === 0) {
			setIsResendDisabled(false);
		}
		return () => clearInterval(interval);
	}, [timer, currentStep]);

	const handleEmailChange = (val) => {
		setEnterEmail(val);
		setEmail(val); // Update the email in HomePage
	};

	const handleResendCode = async () => {
		if (!isResendDisabled) {
			const result = await resend(enterEmail);
			if (result.success) {
				alert('Code sent successfully');
			} else {
				setErrorMessage(result.error);
			}
			setTimer(60);
			setIsResendDisabled(true);
		}
	};

	const handleChangeEmail = () => {
		setCurrentStep(1);
		setErrorMessage('');
	};

	const handleVerifyEmail = async (step) => {
		if (!code.every((digit) => digit !== '')) {
			alert('Please enter all 6 digits of the verification code');
			return;
		} else {
			step3setIsLoading(true);
			const result = await verifyEmail(enterEmail, code.join(''));

			// Handle the result (success or error)
			if (result.success) {
				setCurrentStep(step);
				setErrorMessage(''); // Clear any previous error message
			} else {
				setErrorMessage(`${result.error}`);
			}
			step3setIsLoading(false);
		}
	};

	const handleAddPersonalDetails = async () => {
		try {
			step4setIsLoading(true);
			const stripeResult = await createStripeUser(enterEmail);
			const stripeCustomerId = stripeResult.data.customerId;
			const result = await addPersonalDetails(
				enterEmail,
				firstName,
				lastName,
				phoneNumber,
				_city,
				_state,
				_country,
				_exam,
				stripeCustomerId
			);

			if (result.success) {
				if (typeof closeSignUpModal === 'function') {
					closeSignUpModal();
				}

				dispatch(updateStripeCustomerId(stripeCustomerId));
				setErrorMessage(''); // Clear any previous error message
				if (PURCHASE_TOGGLE) {
					navigate('/plans-and-pricing', {
						state: {
							email: enterEmail,
							isFirstTimeSubscriber: true,
						},
					});
				} else {
					const result = await startFreeTrial(enterEmail);
					if (result.success) {
						const updates = { isRegistrationComplete: true };
						const response = await updateUserDetails(enterEmail, updates, dispatch);
						if (response.success) {
							const userDetails = response.data;
							dispatch(updateIsAuthenticated(true));
							dispatch(updateFirstName(userDetails.first_name));
							dispatch(updateLastName(userDetails.last_name));
							dispatch(updateEmailId(userDetails.email));
							dispatch(updatePhoneNumber(userDetails.phone_no));
							dispatch(updatePlanTierId('FREE_TIER'));
							dispatch(updateCurrentPlanStartDate(new Date().toISOString()));
							dispatch(updateUserId(userDetails.userId));
							dispatch(updateStripeCustomerId(userDetails.stripeCustomerId));
							dispatch(allowAccessToPaymentStatusPage());
							navigate('/payment-success');
						}
					}
				}
			} else {
				setErrorMessage(result.error || stripeResult.error);
			}
		} catch (error) {
			setErrorMessage('An error occurred. Please try again.');
		} finally {
			step4setIsLoading(false);
		}
	};

	const handleRegister = async (step) => {
		if (isStepValid(currentStep)) {
			step2setIsLoading(true);
			const result = await register(username, enterEmail, password);

			// Handle the result (success or error)
			if (result.success) {
				let userId = result?.data?.userId;
				Cookies.set('userId', userId); // Store userId in a cookie
				setCurrentStep(step);
				setErrorMessage(''); // Clear any previous error message
			} else {
				setErrorMessage(`${result.error}`);
			}
			step2setIsLoading(false);
		} else {
			logger.log('Form validation failed');
			alert('Please fill in all required fields correctly');
		}
	};

	const [code, setCode] = useState(new Array(6).fill(''));
	const inputs = useRef([]);

	const [isCodeComplete, setIsCodeComplete] = useState(false);

	const handleChange = (value, index) => {
		if (value.match(/^[0-9]$/)) {
			const newCode = [...code];
			newCode[index] = value;
			setCode(newCode);

			// Move to next input if available
			if (index < 5 && inputs.current[index + 1]) {
				inputs.current[index + 1].focus();
			}

			// Check if all digits are filled
			setIsCodeComplete(newCode.every((digit) => digit !== ''));
		}
	};

	const handleBackspace = (index) => {
		const newCode = [...code];

		// Clear the current input box
		newCode[index] = '';
		setCode(newCode);

		// Update isCodeComplete state when removing digits
		setIsCodeComplete(newCode.every((digit) => digit !== ''));

		// Move focus to the previous box if it's not the first one
		if (index > 0) {
			inputs.current[index - 1].focus();
		}
	};

	const dropdownOptions = [
		{ value: 'option1', label: 'Exam 1' },
		{ value: 'option2', label: 'Exam 2' },
		{ value: 'option3', label: 'Exam 3' },
	];

	const handleSelect = (value) => {
		setFormValidation((prev) => ({
			...prev,
			exam: !!value,
		}));
		logger.log('Selected Value:', value);
	};

	const validatePasswordMatch = (confirmPwd) => {
		if (!confirmPwd) {
			return 'Confirm password is required.';
		} else if (confirmPwd !== password) {
			return 'Passwords do not match.';
		}
		return '';
	};

	const validatePassword = (pwd) => {
		if (!pwd) {
			return 'Password is required.';
		} else if (pwd.length < 8) {
			return 'Password must be at least 8 characters long.';
		}
		// Add more password requirements as needed
		return '';
	};

	// Initialize form validation with all fields as false
	const [formValidation, setFormValidation] = useState({
		username: false,
		email: false,
		password: false,
		confirmPassword: false,
		firstName: false,
		lastName: false,
		phone: false,

		city: false,
		state: false,
		country: false,
		exam: false,
	});

	// Update the handleFieldValidation to include console logging for debugging
	const handleFieldValidation = (field, isValid) => {
		setFormValidation((prev) => {
			const updated = {
				...prev,
				[field]: isValid,
			};
			logger.log(`Field ${field} validation updated:`, isValid);
			logger.log('Current form validation:', updated);
			return updated;
		});
	};

	// Update the isStepValid function to be more strict
	const isStepValid = (step) => {
		if (step === 1) {
			const step1Valid =
				formValidation.username &&
				formValidation.email &&
				formValidation.password &&
				formValidation.confirmPassword;

			return step1Valid;
		} else if (step === 3) {
			const step3Valid = formValidation.firstName && formValidation.lastName;
			// formValidation.phone &&
			// formValidation.city &&
			// formValidation.state &&
			// formValidation.country;

			return step3Valid;
		}
		return true;
	};

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
			}}
		>
			<div className="main_div">
				<div className="left-section">
					<div className="hide-register-text">
						<Label
							label="Register"
							fontSize={'24px'}
							padding={'10px'}
							color={'#545454'}
							fontWeight={'700'}
						/>
						<Label
							label="Register now and feel the New Digital World"
							fontSize={'14px'}
							padding={'10px'}
							color={'#545454'}
							fontWeight={'700'}
						/>

						<Button
							className="log-in-button"
							label="Login"
							style={{ width: '88px' }}
							onClick={openLoginModal}
						>
							<a href="/" />
						</Button>
					</div>
					<Box
						component="img"
						src={signupImage}
						style={{ height: '375px', width: '100%' }}
					/>
				</div>

				<div className="right-section">
					{currentStep == 1 ? (
						<>
							<Label
								label="Sign Up"
								fontSize={'32px'}
								padding={'10px'}
								color={'#545454'}
								fontWeight={'700'}
							/>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									padding: '10px',
									width: '100%',
									maxWidth: '346px',
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										handleRegister(2);
									}
								}}
								tabIndex={0}
							>
								<TextFieldWithImage
									placeholder={'Enter username*'}
									src={userIcon}
									padding={'100px'}
									validate={validateUsername}
									errorMessage="Username must be at least 5 characters long and can only contain letters."
									width="100%"
									value={username}
									onChange={(value) => setUsername(value)}
									onValidation={(isValid) =>
										handleFieldValidation('username', isValid)
									}
									required={true}
									onKeyPress={(e) => {
										if (!/^[a-zA-Z]$/.test(e.key)) {
											e.preventDefault();
										}
									}}
								/>
								<TextFieldWithImage
									placeholder={'Enter Email Address*'}
									src={email}
									padding={'10px'}
									validate={validateEmail}
									errorMessage="Enter a valid email address."
									width="100%"
									value={enterEmail}
									onChange={handleEmailChange}
									onValidation={(isValid) =>
										handleFieldValidation('email', isValid)
									}
								/>
								<TextFieldWithImage
									placeholder={'Enter password*'}
									src={passwordImage}
									padding={'10px'}
									width="100%"
									type="password"
									value={password}
									onChange={(value) => setPassword(value)}
									validate={validatePassword}
									errorMessage="Password must be at least 8 characters long."
									onValidation={(isValid) =>
										handleFieldValidation('password', isValid)
									}
								/>
								<TextFieldWithImage
									placeholder={'Enter confirm password*'}
									src={passwordImage}
									padding={'10px'}
									width="100%"
									type="password"
									value={confirmPassword}
									onChange={(value) => setConfirmPassword(value)}
									validate={(value) => validatePasswordMatch(value)}
									errorMessage="Passwords do not match."
									onValidation={(isValid) =>
										handleFieldValidation('confirmPassword', isValid)
									}
								/>
							</div>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									padding: '10px',
									width: '100%',
									maxWidth: '346px',
								}}
							>
								<Button
									className="next-button-signup"
									label="Next"
									onClick={() => handleRegister(2)}
									isLoading={step2IsLoading}
									disabled={!isStepValid(currentStep) || step2IsLoading}
								></Button>

								{errorMessage && (
									<Typography
										variant="body2"
										color="error"
										style={{ marginTop: '10px', fontSize: '14px' }}
									>
										{errorMessage}
									</Typography>
								)}

								<div className="already-have-an-account">
									<Label
										label="Already have an account?"
										fontSize={'14px'}
										padding={'10px'}
										color={'#545454'}
										fontWeight={'700'}
									/>
									<Button
										className="log-in-button"
										label="Login"
										onClick={openLoginModal}
									>
										<a href="/" />
									</Button>
								</div>
							</div>
						</>
					) : currentStep == 2 ? (
						<>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
								}}
							>
								<div style={{ display: 'flex', justifyContent: 'center' }}>
									<img
										src={email_colored_green}
										alt="icon"
										style={{ width: '34px', height: '29px' }}
									/>
								</div>
								<Label
									label="Verify your Email Address"
									fontSize={'31px'}
									padding={'10px'}
									color={'#545454'}
									fontWeight={'700'}
								/>

								<Label
									label={`A verification code has been sent to ${enterEmail}`}
									fontSize={'16px'}
									padding={'10px'}
									color={'#545454'}
									fontWeight={'700'}
								/>

								<Label
									label={`Please check your inbox and enter the verification code below to verify your address. The code will expire in ${Math.floor(
										timer / 60
									)}:${(timer % 60).toString().padStart(2, '0')}`}
									fontSize={'13px'}
									padding={'10px'}
									color={'#545454'}
									fontWeight={'400'}
								/>

								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										gap: '10px',
									}}
								>
									<div
										style={{ display: 'flex', gap: '5px' }}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												handleVerifyEmail(3);
											}
										}}
										tabIndex={0}
									>
										{code?.map((digit, index) => (
											<input
												key={index}
												type="text"
												value={digit}
												onChange={(e) =>
													handleChange(e.target.value, index)
												}
												onKeyDown={(e) =>
													e.key === 'Backspace' && handleBackspace(index)
												}
												maxLength={1}
												style={{
													width: '40px',
													height: '40px',
													fontSize: '20px',
													textAlign: 'center',
													backgroundColor: '#D9D9D9',
												}}
												ref={(el) => (inputs.current[index] = el)}
											/>
										))}
									</div>

									<Button
										className="next-button-signup"
										label="Verify"
										onClick={() => handleVerifyEmail(3)}
										isLoading={step3IsLoading}
										disabled={!isCodeComplete || step3IsLoading}
										style={{
											width: '346px',
											opacity: isCodeComplete ? 1 : 0.5,
										}}
									/>
									{errorMessage && (
										<Typography
											variant="body2"
											color="error"
											style={{ marginTop: '10px', fontSize: '14px' }}
										>
											{errorMessage}
										</Typography>
									)}

									<div
										style={{
											display: 'flex',
											flexDirection: 'row',
											justifyContent: 'space-between',
											width: '70%',
										}}
									>
										<span
											onClick={handleResendCode}
											style={{
												color: isResendDisabled ? '#999' : '#08BD80',
												cursor: isResendDisabled ? 'default' : 'pointer',
											}}
										>
											{isResendDisabled ? `Resend Code` : 'Resend Code'}
										</span>

										<span
											onClick={handleChangeEmail}
											style={{ color: '#08BD80', cursor: 'pointer' }}
										>
											Change Email
										</span>
									</div>
								</div>
							</div>
						</>
					) : (
						<>
							<div
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
										handleAddPersonalDetails();
									}
								}}
								tabIndex={0}
								style={{
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									width: '100%',
									maxWidth: '346px',
								}}
							>
								<Label
									label="Almost Done!"
									fontSize={'31px'}
									padding={'10px'}
									color={'#545454'}
									fontWeight={'700'}
									width={'100%'}
								/>
								<Label
									label="Enter your Details"
									fontSize={'31px'}
									padding={'10px'}
									color={'#545454'}
									fontWeight={'700'}
									width={'100%'}
								/>

								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'space-between', // Space between items for even placement
										gap: '10px', // Add consistent gap between fields
										width: '100%', // Width of the entire row
										maxWidth: '346px',
										margin: '0 auto', // Center the row within the parent
									}}
								>
									<TextFieldWithImage
										placeholder={'First name*'}
										src={userIcon}
										padding={'100px'}
										value={firstName}
										onChange={(value) => setFirstName(value)}
										validate={validateFnameLname}
										errorMessage="This field cannot be empty"
										width="48%" // Set width of each text field for even spacing
										onValidation={(isValid) =>
											handleFieldValidation('firstName', isValid)
										}
									/>
									<TextFieldWithImage
										placeholder={'Last name*'}
										src={userIcon}
										padding={'100px'}
										value={lastName}
										onChange={(value) => setLastName(value)}
										validate={validateFnameLname}
										errorMessage="This field cannot be empty"
										width="48%" // Set width of each text field for even spacing
										onValidation={(isValid) =>
											handleFieldValidation('lastName', isValid)
										}
									/>
								</div>

								<TextFieldWithImage
									placeholder={'Enter phone number'}
									src={phone}
									padding={'100px'}
									value={phoneNumber}
									onChange={(value) => setPhone(value)}
									// validate={validateMobileNumber}
									// errorMessage='Invalid mobile number'
									width="100%"
									input="tel"
									// onValidation={(isValid) =>
									// 	handleFieldValidation("phone", isValid)
									// }
								/>

								<div
									style={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'space-between', // Space between items for even placement
										gap: '10px', // Add consistent gap between fields
										width: '100%', // Width of the entire row
										maxWidth: '346px',
										margin: '0 auto', // Center the row within the parent
									}}
								>
									<TextFieldWithImage
										placeholder={'Enter city'}
										src={city}
										padding={'100px'}
										value={_city}
										onChange={(value) => setCity(value)}
										// validate={validateFnameLname}
										// errorMessage='This field cannot be empty'
										width="48%" // Set width of each text field for even spacing
										// onValidation={(isValid) =>
										// 	handleFieldValidation("city", isValid)
										// }
									/>
									<TextFieldWithImage
										placeholder={'Enter state'}
										src={city}
										padding={'100px'}
										value={_state}
										onChange={(value) => setState(value)}
										// validate={validateFnameLname}
										// errorMessage='This field cannot be empty'
										width="48%" // Set width of each text field for even spacing
										// onValidation={(isValid) =>
										// 	handleFieldValidation("state", isValid)
										// }
									/>
								</div>
								<TextFieldWithImage
									placeholder={'Country'}
									src={city}
									padding={'100px'}
									value={_country}
									onChange={(value) => setCountry(value)}
									// validate={validateFnameLname}
									// errorMessage='This field cannot be empty'
									width="100%"
									// onValidation={(isValid) =>
									// 	handleFieldValidation("country", isValid)
									// }
								/>

								{/* <DropdownWithImage options={dropdownOptions} placeholder='Exam' src={test} width='75%' value={_exam} onChange={(value) => setExam(value)} onSelect={handleSelect} onValidation={(isValid) => handleFieldValidation("exam", isValid)} /> */}

								<div
									style={{
										justifyContent: 'center',
										alignItems: 'center',
										width: '100%',
										paddingTop: '10px',
									}}
								>
									<Button
										className="next-button-signup"
										label="Next"
										style={{ width: '100%' }}
										onClick={handleAddPersonalDetails}
										isLoading={step4IsLoading}
										disabled={!isStepValid(currentStep) || step4IsLoading}
									></Button>
								</div>
								{errorMessage && (
									<Typography
										variant="body2"
										color="error"
										style={{ marginTop: '10px', fontSize: '14px' }}
									>
										{errorMessage}
									</Typography>
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

const validateUsername = (value) => {
	const hasNonLetters = /[^a-zA-Z]/.test(value);
	if (!value.trim()) {
		return 'Username is required.';
	} else if (value.trim().length < 5) {
		return 'Username must be at least 5 characters long.';
	} else if (hasNonLetters) {
		return 'Username can only contain letters.';
	}
	return '';
};

const validateFnameLname = (value) => {
	if (!value.trim()) {
		return 'User name is required.';
	}
	return '';
};

const validateEmail = (value) => {
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Simple regex to validate email format
	if (!value.trim()) {
		return 'Email is required.';
	} else if (!emailRegex.test(value)) {
		return 'Enter a valid email address.';
	}
	return ''; // No error
};

const validateMobileNumber = (value) => {
	const mobileRegex = /^[0-9]{10}$/; // Adjust regex based on your requirements
	if (!value.trim()) {
		return 'Mobile number is required.';
	} else if (!mobileRegex.test(value)) {
		return 'Enter a valid 10-digit mobile number.';
	}
	return ''; // No error
};

export default SignUp;
